import { LoadingSpinner } from 'components'
import { selectIsLoggedIn } from 'modules/common'
import { selectRequiresBusinessCreation } from 'modules/authentication'
import { useAppSelector } from 'modules/core'
import { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { BusinessMembersService } from 'openapi/services/BusinessMembersService'
import { selectOnboardingCompleted, selectOnboardingDataReady } from '../slices'

/**
 * Does the logged-in user have a pending collaborator invitation? Checked ONLY
 * for users the guard is about to bounce into onboarding — an invited user must
 * land on /accept-invite (join the inviter's business), never in the
 * business-creation wizard or its paywall. This is server truth (not the
 * sign-in response's hasPendingInvite flag) so it survives reloads, OAuth
 * logins, and every other path that skips the login form. The July 2026
 * stranding (Jen/Frontier Kitchen): invitees bounced into the wizard created
 * stray businesses and never joined.
 */
function usePendingInviteCheck(enabled: boolean): { checking: boolean; hasPending: boolean } {
  // `resolved` (not an initial-state snapshot) so `checking` is computed
  // synchronously on EVERY render: when `enabled` flips false→true mid-session
  // (OAuth logins and fresh sessions resolve isLoggedIn/onboardingDataReady
  // after mount), the first enabled render must report checking=true or the
  // guard navigates to /onboarding before the fetch ever runs.
  const [resolved, setResolved] = useState<{ hasPending: boolean } | null>(null)
  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    setResolved(null)
    BusinessMembersService.businessMembershipsControllerFindMyInvitationsV1()
      .then(res => {
        if (cancelled) return
        setResolved({ hasPending: (res.data?.length ?? 0) > 0 })
      })
      .catch(() => {
        // Fail open into the existing behavior (onboarding) — a transient API
        // error must not lock the guard in a spinner.
        if (!cancelled) setResolved({ hasPending: false })
      })
    return () => {
      cancelled = true
    }
  }, [enabled])
  if (!enabled) return { checking: false, hasPending: false }
  return { checking: resolved === null, hasPending: resolved?.hasPending ?? false }
}

/**
 * Guard component that redirects users to onboarding or business creation
 * using render-time navigation (<Navigate>) instead of useEffect.
 *
 * This prevents the "flicker" where users briefly see the chat list
 * before being redirected to onboarding.
 *
 * Two paths into /onboarding:
 *   1. `onboardingCompleted === false` — user hasn't finished the version-N
 *      onboarding (first-time sign-up or version bump).
 *   2. `requiresBusinessCreation === true` — token's bid is
 *      PENDING_BUSINESS_ID, meaning the user has zero businesses. Hits
 *      after they delete or leave their last one. Without this redirect
 *      they'd land on '/' with a token that 401s every business-scoped
 *      call — visually stuck.
 */
export const OnboardingGuard = ({ children }: { children: React.ReactNode }) => {
  const isLoggedIn = useAppSelector(selectIsLoggedIn)
  const onboardingDataReady = useAppSelector(selectOnboardingDataReady)
  const onboardingCompleted = useAppSelector(selectOnboardingCompleted)
  const requiresBusinessCreation = useAppSelector(selectRequiresBusinessCreation)
  const location = useLocation()
  // Only consulted for business-less users (pending bid) the guard would bounce
  // into the wizard — a user who merely has a stale onboarding VERSION but owns
  // a business must still reach the wizard (diverting them too creates
  // accept-or-nothing traps). Never consulted once they're already on
  // /onboarding or /businesses/new (choosing "create my own business" from the
  // accept page is a legitimate way in) or on /accept-invite itself.
  const wouldBounceToOnboarding = !onboardingCompleted || requiresBusinessCreation
  const pendingInviteCheckEnabled = Boolean(
    isLoggedIn &&
    onboardingDataReady &&
    requiresBusinessCreation &&
    !location.pathname.startsWith('/businesses/new') &&
    !location.pathname.startsWith('/accept-invite') &&
    location.pathname !== '/onboarding'
  )
  const pendingInvite = usePendingInviteCheck(pendingInviteCheckEnabled)

  // Not logged in - let routes handle auth
  if (!isLoggedIn) return <>{children}</>

  // Still loading user data - show loading state instead of children
  // Uses persisted user data if available to avoid double spinner
  if (!onboardingDataReady) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // On /onboarding itself, send completed users back to home. Without this
  // a back-nav or direct hit on the URL re-renders the onboarding flow from
  // scratch (Redux progress was cleared on completion, so it'd land them at
  // the first step again). SegwayStep's exit uses replace: true so this
  // path normally shouldn't trigger — this is the belt-and-suspenders layer.
  if (location.pathname === '/onboarding') {
    if (onboardingCompleted && !requiresBusinessCreation) {
      return <Navigate to="/" replace />
    }
    return <>{children}</>
  }

  // Build redirect URL to preserve current path for post-onboarding navigation
  const currentPath = `${location.pathname}${location.search}${location.hash}`
  const redirectParam = currentPath !== '/' ? `?redirectUrl=${encodeURIComponent(currentPath)}` : ''

  // DEV: design preview is reachable mid-onboarding (e.g. conversation hub work).
  if (import.meta.env.DEV && location.pathname === '/_preview') {
    return <>{children}</>
  }

  // An invited user (bid still PENDING → requiresBusinessCreation) who returns
  // to their invite link post-login must see the accept-or-create choice, not be
  // bounced into the business wizard. The page itself offers "Create my own
  // business instead" for those who'd rather onboard; the onboarding-entry
  // prompt remains the net for anyone who loses this redirect entirely.
  if (location.pathname.startsWith('/accept-invite')) {
    return <>{children}</>
  }

  // Either the version-N onboarding isn't done, OR the user has no current
  // business (e.g. just deleted the last one). Both states require landing
  // on the onboarding flow — UNLESS a collaborator invitation is pending, in
  // which case the accept screen comes first (it offers "create my own
  // business instead" for those who want the wizard). Bouncing an invitee into
  // the wizard is the July 2026 stranding bug: they build stray businesses and
  // hit the paywall instead of joining the inviter's business.
  if (wouldBounceToOnboarding) {
    if (pendingInvite.checking) {
      return (
        <div className="flex h-screen w-screen items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      )
    }
    if (pendingInvite.hasPending) {
      return <Navigate to="/accept-invite" replace />
    }
    return <Navigate to={`/onboarding${redirectParam}`} replace />
  }

  // User has completed onboarding - render children
  return <>{children}</>
}
